import { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import moment from "moment";

import WidgetBox from "components/dashboard/widgets/WidgetBox";
import { WidgetProps, ChartPanel } from "types";
import styledConst from "styles";
import useGatewayStatus from "hooks/useGatewayStatus";
import { getGwAndLDSUStatus } from "service/gatewayService";
import { getDateTimeFormat } from "utils/datetime";
import { countSensorsAndActuators } from "utils/gatewayFunctions";
import { DEFAULT_GATEWAY_NAME } from "constant/gateway";
import WidgetContent from "components/dashboard/widgets/WidgetContent";
import LocationMapSection from "./LocationMapSection";

type MultipleGWStatusCardProps = Omit<WidgetProps, "panel"> & {
    panel: ChartPanel;
};

const Status = styled.span<{ isOnline: boolean }>`
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 5px;
    background-color: ${({ isOnline }) => (isOnline ? "#219653" : "#F2495E")};
`;

const StyledWrapper = styled.div`
    padding-top: 30px;
    width: 100%;
    height: 100%;
    display: flex;
    gap: 25px;
    flex-direction: column;
    overflow: hidden;
    justify-content: flex-start;
`;

const GatewayHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    .gateway-header-mute {
        font-size: 14px;
        color: ${styledConst.Primary_Blue_4};
        font-weight: 400;
    }
`;

const GatewayInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0; /* To allow the flex item to shrink properly for text-truncation */
`;

const GatewayIcon = styled.div`
    width: 40px;
    height: 40px;
    background-color: #3c4a67;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
        width: 22px;
        height: 22px;
        color: #ffffff;
    }
`;

const GatewayDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0; /* To allow the flex item to shrink properly for text-truncation */
`;

const OnlineDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;
    text-align: right;
    flex-shrink: 0;
`;

const GatewayName = styled.div`
    margin: 0;
    font-size: 18px;
    font-weight: 500;
    color: #ffffff;
`;

const GatewayId = styled.div`
    font-size: 14px;
    color: ${styledConst.Primary_Blue_4};
    font-weight: 400;
`;

const DevicesSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionTitle = styled.div`
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
`;

const DeviceCards = styled.div`
    display: flex;
    gap: 10px;

    > *:first-child {
        flex-grow: 2;
    }

    > *:last-child {
        flex-grow: 3;
    }
`;

const DeviceCard = styled.div`
    background-color: #3e4b67;
    border-radius: 5px;
    padding: 15px;
    display: flex;
    color: ${styledConst.Primary_Blue_4};
    font-size: 16px;

    > * {
        flex-grow: 1;
    }

    .label {
        color: var(--primary);
        font-size: 20px;
        font-weight: 500;
        margin-bottom: 10px;
    }
`;

const SingleGatewayCard = ({
    panel,
    allRegistry,
}: MultipleGWStatusCardProps) => {
    const refreshRate = 30; // 60 seconds is the interval of gateway heartbeat report, but it's same as the timeout of Websocket, so we set fetch interval to 30 seconds to ensure the connection is alive.
    const gatewayId = panel.attributes[0].gateway_id;
    const showLocation = panel.attributes[0]?.show_location ?? false;
    const containerRef = useRef<HTMLDivElement>(null);
    const [offlineTimestamp, setOfflineTimestamp] = useState<number | null>(
        null
    );

    const onlineStatus = useGatewayStatus(
        panel.attributes?.map((attr) => attr.gateway_id) || [],
        refreshRate
    );
    const onlineData = onlineStatus ? onlineStatus[gatewayId] : null;
    const isOnline = onlineData ? !!onlineData?.isOnline : null;

    const gatewayDetail = useMemo(() => {
        return (
            (allRegistry ?? []).find(
                (gw: any) => gw.gateway_id === gatewayId
            ) ?? {}
        );
    }, [allRegistry, gatewayId]);

    useEffect(() => {
        if (!gatewayId) {
            return;
        }

        const getGatewayOfflineFrom = async () => {
            const res = await getGwAndLDSUStatus(gatewayId);
            if (!res?.data?.heartbeat) {
                return;
            }

            setOfflineTimestamp(res.data.heartbeat);
        };

        if (isOnline === false) {
            getGatewayOfflineFrom();
        } else {
            // Clear the offline timestamp when gateway comes back online
            setOfflineTimestamp(null);
        }
    }, [isOnline, gatewayId]);

    const deviceCounts = useMemo(() => {
        const counts = countSensorsAndActuators(gatewayDetail);

        return {
            ldsu: counts?.ldsuCount ?? null,
            sensor: counts?.sensorCount ?? null,
            actuator: counts?.actuatorCount ?? null,
        };
    }, [gatewayDetail]);

    let onlineStatusLabel = "\u00A0";
    if (isOnline === true) {
        onlineStatusLabel = "Online";
    } else if (isOnline === false) {
        onlineStatusLabel = "Offline";
    }

    return (
        <WidgetBox ref={containerRef} className="widget-box w-100">
            <WidgetContent title={panel.name}>
                <StyledWrapper>
                    <GatewayHeader>
                        <GatewayInfo>
                            <GatewayIcon>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="22"
                                    height="23"
                                    viewBox="0 0 22 23"
                                    fill="none"
                                >
                                    <path
                                        d="M12.837 7.01933C13.8504 7.15363 15.4852 7.44116 17.1158 7.86736C18.6251 8.26188 20.2025 8.79316 21.2674 9.47135L21.4731 9.6089L21.6345 9.72144L21.6937 9.90901C22.2532 11.6725 21.7467 13.2968 21.3947 13.9355L21.3344 14.0457L11.0239 21.7861C10.9907 21.828 10.9514 21.8674 10.9045 21.902C10.7588 22.0095 10.6064 22.0271 10.5316 22.0305C10.3799 22.0373 10.23 21.998 10.1304 21.9679C9.90627 21.9003 9.61283 21.775 9.28687 21.6201C8.62384 21.3049 7.71764 20.8165 6.72457 20.2571C4.73383 19.1358 2.34857 17.7025 0.759903 16.7251L0.661003 16.6637L0.592796 16.5705C0.129651 15.9293 0.00944207 15.0156 0.000535511 14.2447C-0.00865386 13.4461 0.102036 12.6623 0.208566 12.1746L0.274499 11.8711L12.5483 7.05457L12.6881 7L12.837 7.01933ZM1.24872 12.9931C1.19841 13.3598 1.15958 13.7949 1.1646 14.231C1.17228 14.8981 1.27941 15.4582 1.48403 15.8032C3.06428 16.7738 5.36948 18.1572 7.29523 19.242C8.28377 19.7988 9.16202 20.2715 9.78705 20.5686C9.86089 20.6037 9.93108 20.6329 9.99508 20.6618V17.2094C9.07352 16.5862 7.62811 15.6935 6.01522 14.871C4.44483 14.0703 2.75102 13.3514 1.24872 12.9931ZM11.1591 17.2162V20.2298L20.4216 13.2739C20.6112 12.8718 20.865 12.019 20.7479 11.0287L11.1591 17.2162ZM2.65605 12.186C3.96097 12.6073 5.31005 13.2041 6.54382 13.8332C8.16417 14.6594 9.62034 15.5562 10.5817 16.2022L20.0249 10.1102C19.1576 9.68305 18.0147 9.30466 16.8214 8.99277C15.3192 8.60012 13.8085 8.33104 12.8279 8.19475L2.65605 12.186Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M21.3606 12.2815L21.6993 12.7544L10.6305 20.672L0.312012 15.2405L0.853118 14.2106L10.5362 19.3068L21.023 11.8086L21.3606 12.2815Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M11.5394 4.84217C11.3012 4.97111 11.0828 5.13372 10.8911 5.325C10.6979 5.51803 10.5339 5.73821 10.4043 5.97858L11.0083 6.58261C11.4118 5.54822 12.5774 5.03685 13.6117 5.44038C14.1355 5.64467 14.5496 6.0589 14.7539 6.58261L15.358 5.97858C14.6173 4.6103 12.9077 4.10152 11.5394 4.84217Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M10.1308 3.10481C9.893 3.2714 9.67079 3.45919 9.46684 3.6659C9.27687 3.85834 9.10354 4.0664 8.94849 4.28792L9.52539 4.86036C10.7537 3.00452 13.254 2.49581 15.1099 3.72412C15.561 4.02274 15.9474 4.40917 16.246 4.86036L16.823 4.28342C15.3004 2.10994 12.3042 1.58225 10.1308 3.10481Z"
                                        fill="white"
                                    />
                                    <path
                                        d="M8.70377 1.43478C8.47096 1.6158 8.25026 1.81177 8.04282 2.02143C7.84761 2.21312 7.66392 2.41632 7.49292 2.62993L8.06983 3.20689C10.0528 0.552396 13.8123 0.00799976 16.4668 1.99096C16.7118 2.17404 16.9425 2.3756 17.1568 2.59387C17.3462 2.78618 17.5223 2.99097 17.6843 3.20687L18.2612 2.62992C15.952 -0.339297 11.6731 -0.874404 8.70377 1.43478Z"
                                        fill="white"
                                    />
                                </svg>
                            </GatewayIcon>
                            <GatewayDetails>
                                <GatewayName className="text-truncate">
                                    {gatewayDetail?.name ??
                                        DEFAULT_GATEWAY_NAME}
                                </GatewayName>
                                <GatewayId className="text-truncate">
                                    {gatewayId}
                                </GatewayId>
                            </GatewayDetails>
                        </GatewayInfo>
                        <OnlineDetails>
                            <div>
                                {isOnline !== null && (
                                    <Status isOnline={isOnline} />
                                )}
                                {onlineStatusLabel}
                            </div>
                            <div className="gateway-header-mute text-truncate">
                                {!isOnline && offlineTimestamp ? (
                                    <span>
                                        from{" "}
                                        {moment
                                            .unix(offlineTimestamp)
                                            .format(getDateTimeFormat())}
                                    </span>
                                ) : (
                                    "\u00A0"
                                )}
                            </div>
                        </OnlineDetails>
                    </GatewayHeader>

                    <DevicesSection>
                        <SectionTitle>Connected Devices</SectionTitle>
                        <DeviceCards>
                            <DeviceCard>
                                <div>
                                    <div className="label">
                                        {deviceCounts.ldsu ?? "-"}
                                    </div>
                                    <div>LDSUs</div>
                                </div>
                            </DeviceCard>
                            <DeviceCard>
                                <div>
                                    <div className="label">
                                        {deviceCounts.sensor ?? "-"}
                                    </div>
                                    <div>Sensors</div>
                                </div>
                                <div>
                                    <div className="label">
                                        {deviceCounts.actuator ?? "-"}
                                    </div>
                                    <div>Actuators</div>
                                </div>
                            </DeviceCard>
                        </DeviceCards>
                    </DevicesSection>

                    {showLocation && (
                        <LocationMapSection
                            location={gatewayDetail?.location}
                        />
                    )}
                </StyledWrapper>
            </WidgetContent>
        </WidgetBox>
    );
};

export default SingleGatewayCard;
